import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { KeyRound, Pencil, Search, UserPlus, UserRoundCheck, UserRoundX } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { FunctionalSelect, SelectItem } from '../../components/ui/Select';
import { useAuth } from '../../contexts/AuthContext';
import { getRoleCatalog, roleLabel } from '../../services/rolesService';
import { organizationsService } from '../../services/organizationsService';
import { adminUsersApi, apiErrorText, compareUsersByName, fullName } from './adminUsersApi';

const STATUS_FILTERS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All' },
];
const KIND_FILTERS = [
  { value: 'all', label: 'Everyone' },
  { value: 'admin', label: 'Platform admins' },
  { value: 'bulk', label: 'Bulk contributors' },
  { value: 'user', label: 'Regular users' },
];

function matchesSearch(user, q) {
  if (!q) return true;
  const first = (user.first_name || '').toLowerCase();
  const last = (user.last_name || '').toLowerCase();
  const email = (user.email || '').toLowerCase();
  if (q.includes(' ')) {
    const [qFirst, qLast] = q.split(/\s+/, 2);
    return (!qFirst || first.startsWith(qFirst)) && (!qLast || last.startsWith(qLast));
  }
  return first.includes(q) || last.includes(q) || email.includes(q);
}

/** Small round icon button used for the one-click row actions. */
function RowAction({ title, onClick, disabled, tone = 'blue', children }) {
  const tones = {
    blue: 'text-[#0062FB] hover:bg-blue-50',
    pink: 'text-[#F53D7A] hover:bg-pink-50',
    gray: 'text-zinc-500 hover:bg-[#E8EEF5]',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={title}
      className={`rounded p-1.5 disabled:opacity-40 ${tones[tone]}`}
    >
      {children}
    </button>
  );
}

/**
 * Users listing for platform admins: search, filters, and one-click actions per row.
 * Anything heavier (profile, access flags, organizations and roles, password) lives on the
 * detail page — admin and bulk-contributor grants deliberately take more than one click.
 */
function AdminUsersList() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [kind, setKind] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');

  const [confirm, setConfirm] = useState(null); // {title, message, confirmText, run}

  useEffect(() => {
    let cancelled = false;
    Promise.all([adminUsersApi.list(), adminUsersApi.organizations(), getRoleCatalog().catch(() => null)])
      .then(([rows, orgs, cat]) => {
        if (cancelled) return;
        setUsers(Array.isArray(rows) ? rows : []);
        setOrganizations(Array.isArray(orgs) ? orgs : []);
        setCatalog(cat);
      })
      .catch((err) => !cancelled && setError(apiErrorText(err, 'Failed to load users.')))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users
      .filter((u) => (status === 'all' ? true : status === 'active' ? u.is_active : !u.is_active))
      .filter((u) => {
        if (kind === 'admin') return u.is_admin;
        if (kind === 'bulk') return u.is_bulk_contributor;
        if (kind === 'user') return !u.is_admin && !u.is_bulk_contributor;
        return true;
      })
      .filter((u) => orgFilter === 'all' || (u.organizations || []).includes(orgFilter))
      .filter((u) => matchesSearch(u, q))
      .sort(compareUsersByName);
  }, [users, search, status, kind, orgFilter]);

  const patchUser = (id, patch) => setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));

  const run = async (id, fn, successMessage, failMessage) => {
    setBusyId(id);
    try {
      await fn();
      if (successMessage) toast.success(successMessage);
      organizationsService.clearOrganizationsListCookie();
    } catch (err) {
      toast.error(apiErrorText(err, failMessage));
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = (u) => {
    if (u.is_active) {
      setConfirm({
        title: 'Deactivate user',
        message: `Deactivate ${u.email}? They will no longer be able to sign in and their API keys stop working.`,
        confirmText: 'Deactivate',
        run: () =>
          run(u.id, async () => {
            await adminUsersApi.setActive(u.id, false);
            patchUser(u.id, { is_active: false });
          }, 'User deactivated', 'Failed to deactivate user.'),
      });
      return;
    }
    run(u.id, async () => {
      await adminUsersApi.setActive(u.id, true);
      patchUser(u.id, { is_active: true });
    }, 'User activated', 'Failed to activate user.');
  };

  const resetPassword = (u) =>
    setConfirm({
      title: 'Send password reset',
      message: `Email ${u.email} a new temporary password? Their current password stops working.`,
      confirmText: 'Send',
      run: () => run(u.id, () => adminUsersApi.sendPasswordReset(u.id), 'Password reset email sent', 'Failed to reset password.'),
    });

  const membershipText = (u) => {
    const ms = Array.isArray(u.memberships) ? u.memberships : [];
    if (ms.length === 0) return (u.organizations || []).join(', ') || '—';
    return ms
      .map((m) => {
        const roles = (m.roles || []).map((r) => roleLabel(catalog, r));
        return roles.length ? `${m.organization_name} (${roles.join(', ')})` : `${m.organization_name} (no roles)`;
      })
      .join(' · ');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle>
            Users
            {!loading ? <span className="ml-2 text-sm font-normal text-zinc-500">({visible.length} of {users.length})</span> : null}
          </CardTitle>
          <Button variant="outline" onClick={() => navigate('/admin/users/new')} className="gap-1.5">
            <UserPlus className="h-4 w-4" />
            Add user
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative min-w-[16rem] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              aria-label="Search users"
            />
          </div>
          <FunctionalSelect label="Status" className="w-40" value={status} onValueChange={setStatus}>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </FunctionalSelect>
          <FunctionalSelect label="Type" className="w-52" value={kind} onValueChange={setKind}>
            {KIND_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </FunctionalSelect>
          <FunctionalSelect label="Org" className="w-52" value={orgFilter} onValueChange={setOrgFilter} placeholder="All">
            <SelectItem value="all">All organizations</SelectItem>
            {[...organizations]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((o) => (
                <SelectItem key={o.id} value={o.name}>
                  {o.name}
                </SelectItem>
              ))}
          </FunctionalSelect>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="py-8 text-center text-sm text-gray-500">Loading users…</p>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-600">{error}</p>
        ) : visible.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No users match.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-zinc-500">
                  <th className="py-2 pr-3 font-medium">User</th>
                  <th className="py-2 pr-3 font-medium">Organizations &amp; roles</th>
                  <th className="py-2 pr-3 font-medium">Access</th>
                  <th className="py-2 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => {
                  const isSelf = String(u.id) === String(me?.id);
                  const busy = busyId === u.id;
                  return (
                    <tr key={u.id} className={`border-b border-gray-100 ${u.is_active ? '' : 'opacity-60'}`}>
                      <td className="py-2 pr-3 align-top">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/users/${u.id}`)}
                          className="text-left font-medium text-[#18181B] hover:text-[#0062FB] hover:underline"
                        >
                          {fullName(u) || u.email}
                        </button>
                        <div className="text-xs text-zinc-500">
                          {u.email}
                          {u.title ? ` · ${u.title}` : ''}
                        </div>
                      </td>
                      <td className="max-w-[28rem] py-2 pr-3 align-top text-zinc-600">{membershipText(u)}</td>
                      <td className="py-2 pr-3 align-top">
                        <div className="flex flex-wrap gap-1">
                          <span className={`rounded-full px-2 py-0.5 text-xs ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {u.is_active ? 'Active' : 'Inactive'}
                          </span>
                          {u.is_admin ? <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-800">Admin</span> : null}
                          {u.is_bulk_contributor ? (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">Bulk Contributor</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="py-2 align-top text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <RowAction title="Edit user" onClick={() => navigate(`/admin/users/${u.id}`)} disabled={busy}>
                            <Pencil className="h-4 w-4" />
                          </RowAction>
                          <RowAction title="Send password reset email" onClick={() => resetPassword(u)} disabled={busy || !u.is_active} tone="gray">
                            <KeyRound className="h-4 w-4" />
                          </RowAction>
                          <RowAction
                            title={u.is_active ? 'Deactivate user' : 'Activate user'}
                            onClick={() => toggleActive(u)}
                            disabled={busy || isSelf}
                            tone={u.is_active ? 'pink' : 'blue'}
                          >
                            {u.is_active ? <UserRoundX className="h-4 w-4" /> : <UserRoundCheck className="h-4 w-4" />}
                          </RowAction>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <ConfirmDialog
        isOpen={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm?.run()}
        title={confirm?.title || 'Confirm'}
        message={confirm?.message || ''}
        confirmText={confirm?.confirmText || 'OK'}
        cancelText="Cancel"
      />
    </Card>
  );
}

export default AdminUsersList;
